import React, { useEffect, useState, useCallback, useRef } from "react";

import { useSnackbar } from "notistack";
import { useStore } from "../../context/StoreContext";
import { api } from "../../hooks/apiClient";
import OrderMsgDialog from "./components/OrderMsgDialog";
import Messages from "./components/MessagesSide";
import {
  Spinner,
  ChatGlyph,
  RefreshGlyph,
  SearchGlyph,
  EmptyState,
  iconBtn,
  formatDate,
} from "./components/ChatHomeUiPieces";
import {
  mockGetConversations,
  mockSearchConversations,
  mockGetMessages,
} from "./mockWhatsapp";
import ConnectWhatsApp from "./components/ConnectWhatsapp";

const USE_MOCK = true; // set to false to use the real API
const PAGE_LIMIT = 50;

/* ---------- page ---------- */

const ChatHome = () => {
  const { enqueueSnackbar } = useSnackbar();
  const messagesEndRef = useRef(null);
  const { selectedStore, isStoreSelected } = useStore();

  const storePhoneId = isStoreSelected ? selectedStore?.phoneID : null;
  const whatsappDisabled = isStoreSelected && !selectedStore?.isWhatsappEnabled;

  const [orderDialog, setOrderDialog] = useState(false);
  const [chats, setChats] = useState([]);
  const [tempChats, setTempChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_LIMIT,
    hasMore: false,
    total: 0,
    totalPages: 0,
  });

  const toggleOpenDialog = () => setOrderDialog((p) => !p);

  const getAllConversations = useCallback(
    async (phoneId, page = 1, limit = PAGE_LIMIT) => {
      if (!phoneId) {
        enqueueSnackbar("Phone ID is required", { variant: "error" });
        return;
      }
      setLoading(true);
      try {
        const { data } = USE_MOCK
          ? { data: await mockGetConversations(page, limit) }
          : await api.get(`whatsapp/conversations/${phoneId}`, {
              params: { page, limit },
            });

        const { chats: newChats, total, totalPages, hasMore } = data;
        setChats(newChats);
        setTempChats(newChats);
        setPagination((prev) => ({
          ...prev,
          page,
          total,
          totalPages,
          hasMore: hasMore !== undefined ? hasMore : page < totalPages,
        }));
      } catch (error) {
        console.error("Error fetching chats:", error);
        enqueueSnackbar(
          error.response?.data?.error || "Failed to fetch chats",
          {
            variant: "error",
          },
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getSearchConversation = async () => {
    if (!storePhoneId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `whatsapp/search/conversations/${storePhoneId}`,
        { params: { search } },
      );
      setChats(data.chats);
      setPagination((prev) => ({
        ...prev,
        page: 1,
        total: data.total,
        totalPages: data.totalPages,
        hasMore: data.hasMore,
      }));
    } catch (error) {
      console.error("Error searching chats:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchChat = () => {
    if (!search.trim()) {
      setChats(tempChats);
      return;
    }
    getSearchConversation();
  };

  const refreshChats = () => {
    if (storePhoneId) getAllConversations(storePhoneId, 1, pagination.limit);
  };

  // load conversations when the store changes (only if WhatsApp is enabled)
  useEffect(() => {
    setSelectedChat(null);
    setSearch("");
    setChats([]);
    setTempChats([]);
    // if (whatsappDisabled || !storePhoneId) return;
    getAllConversations(storePhoneId, 1, PAGE_LIMIT);
  }, [selectedStore?.id, storePhoneId, whatsappDisabled, getAllConversations]);

  // load messages for the selected chat
  useEffect(() => {
    if (!selectedChat) return;
    let cancelled = false;
    setMsgLoading(true);
    (async () => {
      try {
        const response = await api.get(`whatsapp/messages/${selectedChat.id}`, {
          params: { conversationId: selectedChat.id },
        });
        if (cancelled) return;
        const updatedChat = {
          ...selectedChat,
          unreadCount: 0,
          Messages: response.data,
        };
        setSelectedChat(updatedChat);
        setChats((prev) =>
          prev.map((c) => (c.id === selectedChat.id ? updatedChat : c)),
        );
      } catch (err) {
        console.error("Failed to load messages:", err);
      } finally {
        if (!cancelled) setMsgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedChat?.id]);

  // scroll to the newest message
  useEffect(() => {
    if (msgLoading || !messagesEndRef.current || !selectedChat?.Messages)
      return;
    requestAnimationFrame(() =>
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      }),
    );
  }, [selectedChat?.Messages, msgLoading]);

  /* ---------- guarded states ---------- */

  if (!isStoreSelected) {
    return (
      <div className="flex h-[95vh] flex-col rounded-xl border border-slate-200 bg-white">
        <EmptyState
          icon={<ChatGlyph className="h-6 w-6" />}
          title="No store selected"
        >
          Select a store to see its WhatsApp conversations.
        </EmptyState>
      </div>
    );
  }

  if (whatsappDisabled) {
    return (
      <>
        {!storePhoneId ? (
          <ConnectWhatsApp />
        ) : (
          <div className="flex h-[95vh] flex-col rounded-xl border border-slate-200 bg-white">
            <EmptyState
              icon={<ChatGlyph className="h-6 w-6" />}
              title="WhatsApp module is turned off"
            >
              Enable the WhatsApp module for{" "}
              <span className="font-medium text-slate-700">
                {selectedStore?.public_name}
              </span>{" "}
              in the Stores menu to start using conversations.
            </EmptyState>
          </div>
        )}
      </>
    );
  }

  /* ---------- main view ---------- */

  return (
    <div className="flex h-[95vh] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
      <OrderMsgDialog open={orderDialog} ToggleOpen={toggleOpenDialog} />

      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <ChatGlyph className="h-5 w-5 text-slate-500" />
          <h1 className="text-sm font-semibold text-slate-900">
            WhatsApp conversations
          </h1>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleOpenDialog}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            Order messages
          </button>
          <button
            type="button"
            onClick={refreshChats}
            disabled={loading}
            aria-label="Refresh conversations"
            className={iconBtn}
          >
            <RefreshGlyph
              className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Conversation list */}
        <aside
          className={`${
            selectedChat ? "hidden md:flex" : "flex"
          } w-full flex-col border-r border-slate-200 md:w-[380px] md:flex-none`}
        >
          {/* Search */}
          <div className="border-b border-slate-200 p-3">
            <div className="relative">
              <SearchGlyph className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                inputMode="numeric"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!e.target.value.trim()) setChats(tempChats);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    searchChat();
                  }
                }}
                placeholder="Search by number, press Enter"
                className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
            </div>
          </div>

          {/* List */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center p-6">
                <Spinner className="h-6 w-6" />
              </div>
            ) : chats.length === 0 ? (
              <EmptyState
                icon={<ChatGlyph className="h-6 w-6" />}
                title="No conversations yet"
              >
                Conversations will appear here once customers message you.
              </EmptyState>
            ) : (
              <ul className="divide-y divide-slate-100">
                {chats.map((chat) => {
                  const active = selectedChat?.id === chat.id;
                  const last = chat.Messages?.[chat.Messages.length - 1];
                  const status = chat.Messages?.[0]
                    ? chat.Messages[0].status_status || chat.Messages[0].status
                    : null;
                  return (
                    <li key={chat.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedChat(chat)}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition focus:outline-none focus-visible:bg-slate-50 ${
                          active ? "bg-slate-100" : "hover:bg-slate-50"
                        }`}
                      >
                        <div className="relative flex-none">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                            <ChatGlyph className="h-5 w-5" />
                          </div>
                          {chat.unreadCount > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1 text-[11px] font-semibold text-white">
                              {chat.unreadCount}
                            </span>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-900">
                            {chat.customerNumber}
                          </p>
                          <p className="truncate text-sm text-slate-500">
                            {last ? last.content : "No messages"}
                          </p>
                        </div>

                        <div className="flex flex-none flex-col items-end gap-1">
                          <span className="text-xs text-slate-400">
                            {formatDate(chat.updatedAt)}
                          </span>
                          {status && (
                            <span
                              className={`rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
                                status === "read"
                                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                  : "bg-slate-50 text-slate-600 ring-slate-200"
                              }`}
                            >
                              {status}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-sm">
              <button
                type="button"
                disabled={loading || pagination.page <= 1}
                onClick={() =>
                  getAllConversations(
                    storePhoneId,
                    pagination.page - 1,
                    pagination.limit,
                  )
                }
                className="rounded-lg px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-slate-500">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={loading || pagination.page >= pagination.totalPages}
                onClick={() =>
                  getAllConversations(
                    storePhoneId,
                    pagination.page + 1,
                    pagination.limit,
                  )
                }
                className="rounded-lg px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </aside>

        {/* Message view */}
        <section
          className={`${
            selectedChat ? "flex" : "hidden md:flex"
          } min-w-0 flex-1 flex-col`}
        >
          <Messages
            setChats={setChats}
            selectedChat={selectedChat}
            setSelectedChat={setSelectedChat}
            storePhoneId={storePhoneId}
            msgLoading={msgLoading}
          />
        </section>
      </div>
    </div>
  );
};

export default ChatHome;
